import * as ReactDOM from 'react-dom';
import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import L from 'leaflet';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import lodashGroupBy from 'lodash/groupBy';
import lodashOrderBy from 'lodash/orderBy';
import lodashKeyBy from 'lodash/keyBy';
import { useHubConnection } from '../hubConnection';
import * as storeExt from '../store';
import {
    CollapsiblePanel,
    CollapsiblePanelHeader,
    Tabs,
    ProjectTagsFilter,
} from '../components';

import { useTitle, cpqDataState } from '../utils';
import { ProductDetailTabs } from '../components/productDetailTabs';
import { ProjectHeader } from '../components/ProjectHeader';
import { SelectField, Modal } from '../components';
import {
    getItemValidate,
    sameAsValidatedResult,
} from '../components/validateUtils';
import { TagsTable } from '../components/TagsTable';
import { CpqModal } from '../components/modals/CpqModal';
import { NewTagModal } from '../components/modals/NewTagModal';
import { ValidateWarningModal } from '../components/modals/ValidateWarningModal';
import { ValidatingModal } from '../components/modals/ValidatingModal';
import { SaveValidationModal } from '../components/modals/SaveValidationModal';
import { EditSkuModal } from '../components/modals/EditSkuModal';

const {
    useGetConfigQuery,
    useGetProjectQuery,
    useUpdateProjectMutation,
    useUploadDrawingsMutation,
    useGetDrawingsQuery,
    useDeleteDrawingMutation,
    useUpdateDrawingMutation,
    useCreateOverlayMarkerMutation,
    useDeleteOverlayMarkerMutation,
    useGetProjectReadyQuery,
    useGetProjectFreightQuery,
    useDeleteMultipleProjectTagItemsMutation,
    useDeleteProjectTagMutation,
    mainActions,
    documentsApi,
    projectsApi,
    mainSelectors,
} = storeExt;

const Drawings = (props) => {
    const { projectId } = props;

    const { data: project } = useGetProjectQuery(projectId);
    const { data: drawings } = useGetDrawingsQuery(projectId);
    const selectedItemId = useSelector(mainSelectors.getSelectedItemId);

    const [showEditNameModal, setShowEditNameModal] = React.useState(false);
    const [selectedDrawingId, setSelectedDrawingId] = React.useState('');
    const [leafletMap, setLeafletMap] = React.useState(null);
    const [drawingMarkerEls, setDrawingMarkerEls] = React.useState([]);

    const mapDivRef = React.useRef(null);
    const fileInputRef = React.useRef(null);

    const dispatch = useDispatch();

    const [uploadDrawings] = useUploadDrawingsMutation();
    const [deleteDrawing] = useDeleteDrawingMutation();
    const [updateDrawing] = useUpdateDrawingMutation();
    const [createOverlayMarker] = useCreateOverlayMarkerMutation();
    const [deleteOverlayMarker] = useDeleteOverlayMarkerMutation();

    const drawingsMap = React.useMemo(
        () => (drawings ? lodashKeyBy(drawings, 'id') : null),
        [drawings],
    );

    const selectedTag = React.useMemo(() => {
        return project?.tags?.find((x) =>
            x.items.map((y) => y.id).includes(selectedItemId),
        );
    }, [project, selectedItemId]);

    const firstItemIdForTagId = React.useCallback(
        (tagId) => project?.tags?.find((x) => x.id === tagId)?.items?.[0]?.id,
        [project],
    );

    const selectedDrawing = React.useMemo(
        () =>
            selectedDrawingId.length && drawingsMap
                ? drawingsMap[selectedDrawingId]
                : null,
        [drawingsMap, selectedDrawingId],
    );

    React.useEffect(() => {
        if (!leafletMap) return;

        leafletMap.on('click', (e) => {
            if (selectedTag) {
                const { lat, lng } = e.latlng;

                const yPos = 100 - (lat / selectedDrawing.height) * 100;
                const xPos = (lng / selectedDrawing.width) * 100;
                createOverlayMarker({
                    projectId: projectId,
                    drawingId: selectedDrawing.id,
                    body: {
                        tagId: selectedTag.id,
                        positionX: xPos.toString(),
                        positionY: yPos.toString(),
                    },
                }).unwrap();
            } else {
                dispatch(
                    mainActions.addAlert({
                        kind: 'info',
                        msg: 'Select a tag item to place an overlay marker',
                    }),
                );
            }
        });

        return () => {
            leafletMap.off('click');
        };
    }, [
        leafletMap,
        projectId,
        selectedTag,
        selectedDrawing,
        createOverlayMarker,
        dispatch,
    ]);

    React.useEffect(() => {
        if (!selectedDrawing || !projectId) return;

        const width = selectedDrawing.width;
        const height = selectedDrawing.height;
        const imageBounds = [
            [0, 0],
            [height, width],
        ];

        const map = L.map(mapDivRef.current, {
            crs: L.CRS.Simple,
            zoomSnap: 0.1,
            minZoom: -3,
            maxZoom: 3,
            maxBounds: L.latLngBounds(L.latLng(0, 0), L.latLng(height, width)),
        });

        L.DomUtil.addClass(map.getContainer(), '!cursor-pointer');

        map.fitBounds(imageBounds);

        const markerGroup = L.layerGroup().addTo(map);

        let dmes = [];
        let observers = [];

        for (const overlayMarker of selectedDrawing.overlayMarkers) {
            const yPos =
                ((100 - Number.parseFloat(overlayMarker.positionY)) / 100) *
                height;
            const xPos =
                (Number.parseFloat(overlayMarker.positionX) / 100) * width;
            const marker = L.marker([yPos, xPos], {
                icon: L.divIcon({ className: 'hidden' }),
            }).addTo(markerGroup);

            const markerHtml = (
                <div
                    className="flex items-center justify-between"
                    key={overlayMarker.id}
                >
                    <div
                        className="flex items-center justify-between"
                        onClick={() => {
                            dispatch(
                                mainActions.setSelectedItemId(
                                    firstItemIdForTagId(overlayMarker.tagId),
                                ),
                            );
                        }}
                    >
                        <FontAwesomeIcon
                            className="mr-4"
                            icon="fa-solid fa-tags"
                        />
                        <div className="text-ellipsis">
                            {
                                project?.tags?.find(
                                    (y) => y.id === overlayMarker.tagId,
                                )?.value
                            }
                        </div>
                    </div>
                    <div className="border-l border-white ml-4 pl-4">
                        <FontAwesomeIcon
                            icon="fa-solid fa-xmark"
                            onClick={() => {
                                deleteOverlayMarker({
                                    projectId: projectId,
                                    drawingId: selectedDrawing.id,
                                    overlayMarkerId: overlayMarker.id,
                                }).unwrap();
                            }}
                        />
                    </div>
                </div>
            );

            const popupContent = document.createElement('div');

            const popup = L.popup({ className: 'local-popup' }).setContent(
                popupContent,
            );

            marker
                .bindPopup(popup, {
                    closeButton: false,
                    autoClose: false,
                    closeOnClick: false,
                    closeOnEscapeKey: false,
                })
                .openPopup();

            const observer = new MutationObserver((m, o) => {
                popup.update();
            });

            observer.observe(popupContent, { childList: true });
            observers.push(observer);

            dmes.push(ReactDOM.createPortal(markerHtml, popupContent));
        }

        setDrawingMarkerEls(dmes);

        L.imageOverlay(
            `/api/projects/${projectId}/drawings/${selectedDrawing.id}`,
            imageBounds,
        ).addTo(map);

        setLeafletMap(map);

        return () => {
            observers.forEach((x) => x.disconnect());
            map.remove();
            setDrawingMarkerEls([]);
        };
    }, [
        selectedDrawing,
        projectId,
        project?.tags,
        setLeafletMap,
        setDrawingMarkerEls,
        dispatch,
        firstItemIdForTagId,
        deleteOverlayMarker,
    ]);

    const orderedDrawingOptions = React.useMemo(
        () => [
            { value: '', name: '' },
            ...((drawings && lodashOrderBy(drawings, 'sequenceId'))?.map(
                (x, idx) => ({
                    value: x.id,
                    name: x.label,
                }),
            ) ?? []),
        ],
        [drawings],
    );

    const nextDrawingId = React.useMemo(() => {
        if (!orderedDrawingOptions) return null;

        const idx = orderedDrawingOptions.findIndex(
            (x) => x.value === selectedDrawingId,
        );
        return orderedDrawingOptions?.[idx + 1]?.value ?? null;
    }, [selectedDrawingId, orderedDrawingOptions]);

    const prevDrawingId = React.useMemo(() => {
        if (!orderedDrawingOptions) return null;

        const idx = orderedDrawingOptions.findIndex(
            (x) => x.value === selectedDrawingId,
        );
        return orderedDrawingOptions?.[idx - 1]?.value ?? null;
    }, [selectedDrawingId, orderedDrawingOptions]);

    const handleFileChange = (e) => {
        e.preventDefault();

        const files = e.target.files;

        if (files.length > 0) {
            const form = new FormData();

            for (let file of files) {
                form.append('files', file);
            }

            uploadDrawings({ projectId: projectId, body: form }).unwrap();
        }
    };

    return (
        <div className="bg-white border border-black-20 p-4">
            <input
                ref={fileInputRef}
                hidden
                type="file"
                onChange={handleFileChange}
            />
            <div className="flex items-center justify-end mb-4">
                <p className="mr-auto">Select drawing</p>
                <FontAwesomeIcon
                    className="ml-4 cursor-pointer"
                    onClick={() => fileInputRef.current.click()}
                    icon="fa-solid fa-arrow-up-from-bracket"
                />
                <FontAwesomeIcon
                    className="ml-4 cursor-pointer"
                    onClick={() => {
                        if (
                            selectedDrawing &&
                            window.confirm('Are you sure?')
                        ) {
                            deleteDrawing({
                                projectId: projectId,
                                id: selectedDrawing.id,
                            }).unwrap();
                        }
                    }}
                    icon="fa-solid fa-trash-can"
                />
                <FontAwesomeIcon
                    className="ml-4 cursor-pointer"
                    onClick={() =>
                        selectedDrawing && setShowEditNameModal(true)
                    }
                    icon="fa-solid fa-pen-to-square"
                />
                <FontAwesomeIcon
                    className="ml-4 cursor-pointer"
                    onClick={() =>
                        prevDrawingId && setSelectedDrawingId(prevDrawingId)
                    }
                    icon="fa-solid fa-chevron-left"
                />
                <FontAwesomeIcon
                    className="ml-4 cursor-pointer"
                    onClick={() =>
                        nextDrawingId && setSelectedDrawingId(nextDrawingId)
                    }
                    // onClick={() => fileInputRef.current.click()}
                    icon="fa-solid fa-chevron-right"
                />
            </div>
            <div className="mb-4">
                <SelectField
                    value={selectedDrawingId}
                    onChange={(option) => setSelectedDrawingId(option.value)}
                    options={orderedDrawingOptions}
                />
            </div>
            <div>
                <div
                    ref={mapDivRef}
                    style={{ height: '600px' }}
                ></div>
            </div>
            <Modal
                fitContent
                show={showEditNameModal}
            >
                <div className="sm:container p-4 min-w-[600px]">
                    <h1>Update drawing</h1>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            updateDrawing({
                                projectId: projectId,
                                id: selectedDrawing?.id,
                                label: e.target.label.value,
                            })
                                .unwrap()
                                .then(() => setShowEditNameModal(false));
                        }}
                    >
                        <div className="pb-4">
                            <div className="swegon-ui-text-input-wrapper">
                                <input
                                    type="text"
                                    name="label"
                                    defaultValue={selectedDrawing?.label}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button
                                className="swegon-like-button outlined mr-4 !px-4 amber"
                                type="button"
                                onClick={() => setShowEditNameModal(false)}
                            >
                                Close
                            </button>
                            <button
                                className="swegon-like-button contained !px-4"
                                type="submit"
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
            {drawingMarkerEls}
        </div>
    );
};

const Tags = (props) => {
    const {
        tags: propsTags,
        projectId,
        checkedItemIds,
        checkedTagIds,
        onItemCheckboxToggle,
        onTagCheckboxToggle,
    } = props;

    const tags = lodashOrderBy(propsTags ?? [], 'sequenceId');

    return (
        <CollapsiblePanel
            defaultOpen
            bordered
            header={CollapsiblePanelHeader}
            title={`Tags (${tags.length})`}
            showWarning={tags.some((x) =>
                x.items.some((y) => y.needValidate || y.needAction),
            )}
        >
            <div className="mb-2">
                <TagsTable
                    isDraggable={true}
                    tags={tags}
                    projectId={projectId}
                    checkedItemIds={checkedItemIds}
                    checkedTagIds={checkedTagIds}
                    onItemCheckboxToggle={onItemCheckboxToggle}
                    onTagCheckboxToggle={onTagCheckboxToggle}
                />
            </div>
        </CollapsiblePanel>
    );
};

const EquipmentType = (props) => {
    const {
        tags: propsTags,
        projectId,
        checkedItemIds,
        checkedTagIds,
        onItemCheckboxToggle,
        onTagCheckboxToggle,
    } = props;

    const groups = lodashGroupBy(
        lodashOrderBy(propsTags ?? [], 'sequenceId'),
        'equipmentType',
    );

    return (
        <>
            {Object.entries(groups).map(([name, tags]) => (
                <CollapsiblePanel
                    bordered
                    header={CollapsiblePanelHeader}
                    title={`${name} (${tags.length})`}
                    key={name}
                    showWarning={tags.some((x) =>
                        x.items.some((y) => y.needValidate || y.needAction),
                    )}
                >
                    <div className="mb-2">
                        <TagsTable
                            tags={tags}
                            projectId={projectId}
                            checkedItemIds={checkedItemIds}
                            checkedTagIds={checkedTagIds}
                            onItemCheckboxToggle={onItemCheckboxToggle}
                            onTagCheckboxToggle={onTagCheckboxToggle}
                        />
                    </div>
                </CollapsiblePanel>
            ))}
        </>
    );
};

const Room = (props) => {
    const {
        tags: propsTags,
        projectId,
        checkedItemIds,
        checkedTagIds,
        onItemCheckboxToggle,
        onTagCheckboxToggle,
    } = props;

    const groups = lodashGroupBy(
        lodashOrderBy(propsTags ?? [], 'sequenceId'),
        'room',
    );

    return (
        <>
            {Object.entries(groups).map(([name, tags]) => (
                <CollapsiblePanel
                    bordered
                    header={CollapsiblePanelHeader}
                    title={`${name} (${tags.length})`}
                    key={name}
                    showWarning={tags.some((x) =>
                        x.items.some((y) => y.needValidate || y.needAction),
                    )}
                >
                    <div className="mb-2">
                        <TagsTable
                            tags={tags}
                            projectId={projectId}
                            checkedItemIds={checkedItemIds}
                            checkedTagIds={checkedTagIds}
                            onItemCheckboxToggle={onItemCheckboxToggle}
                            onTagCheckboxToggle={onTagCheckboxToggle}
                        />
                    </div>
                </CollapsiblePanel>
            ))}
        </>
    );
};

const Trade = (props) => {
    const {
        tags: propsTags,
        projectId,
        checkedItemIds,
        checkedTagIds,
        onItemCheckboxToggle,
        onTagCheckboxToggle,
    } = props;

    const groups = lodashGroupBy(
        lodashOrderBy(propsTags ?? [], 'sequenceId'),
        (x) => x.trade || '',
    );

    return (
        <>
            {Object.entries(groups).map(([name, tags]) => (
                <CollapsiblePanel
                    bordered
                    header={CollapsiblePanelHeader}
                    title={`${name ?? ''} (${tags.length})`}
                    key={name ?? ''}
                    showWarning={tags.some((x) =>
                        x.items.some((y) => y.needValidate || y.needAction),
                    )}
                >
                    <div className="mb-2">
                        <TagsTable
                            tags={tags}
                            projectId={projectId}
                            checkedItemIds={checkedItemIds}
                            checkedTagIds={checkedTagIds}
                            onItemCheckboxToggle={onItemCheckboxToggle}
                            onTagCheckboxToggle={onTagCheckboxToggle}
                        />
                    </div>
                </CollapsiblePanel>
            ))}
        </>
    );
};

const Project = () => {
    const dispatch = useDispatch();

    const [cpqData, setCpqData] = cpqDataState.useStateContext();

    const { projectId } = useParams();
    const { data: config } = useGetConfigQuery();
    const { data: project } = useGetProjectQuery(projectId);
    const [updateProject] = useUpdateProjectMutation();
    const { data: projectIsReady } = useGetProjectReadyQuery(projectId);
    const { data: projectFreight } = useGetProjectFreightQuery(projectId);
    const [deleteMultipleTagItems] = useDeleteMultipleProjectTagItemsMutation();
    const [deleteTag] = useDeleteProjectTagMutation();

    const [newTagModalOpen, setNewTagModalOpen] = React.useState(false);
    const [isUpdatingProjectName, setIsUpdatingProjectName] =
        React.useState(false);
    const [updatedProjectName, setUpdatedProjectName] = React.useState('');

    //tags to be seen by the user based on filter
    const [filteredTags, setFilteredTags] = useState(project?.tags);
    const [tagSearchTerm, setTagSearchTerm] = useState('');
    const [showWarnValidateModal, setShowWarnValidateModal] =
        React.useState(false);
    const [showValidatingModal, setShowValidatingModal] = React.useState(false);
    const [itemsAlreadyValidated, setItemsAlreadyValidated] = React.useState(0);
    const [showSaveValidationModal, setShowSaveValidationModal] =
        React.useState(false);
    const [validationData, setValidationData] = React.useState({});
    const [toValidateItems, setToValidateItems] = React.useState('');
    const [freight, setFreight] = React.useState('');
    const [projectCountry, setProjectCountry] = React.useState('');
    const [checkedItemIds, setCheckedItemIds] = React.useState(new Set());
    const [checkedTagIds, setCheckedTagIds] = React.useState(new Set());
    const [editSkuModalOpen, setEditSkuModalOpen] = React.useState(false);

    useTitle(React.useMemo(() => project?.name ?? 'Project', [project]));

    React.useEffect(() => {
        localStorage.setItem('Validating', '0');
        localStorage.setItem('ToValidate', '');
    }, []);

    React.useEffect(() => {
        if (!project) return;
        setUpdatedProjectName(project.name);
        setProjectCountry(project.country);
        setFreight('');
    }, [project]);

    React.useEffect(() => {
        setFilteredTags(project?.tags);
    }, [project?.tags]);

    const searchedTags = React.useMemo(() => {
        const normalizedSearch = tagSearchTerm.trim().toLowerCase();
        if (!normalizedSearch.length) return filteredTags;

        return (filteredTags ?? []).filter((tag) =>
            (tag?.value ?? '').toLowerCase().includes(normalizedSearch),
        );
    }, [filteredTags, tagSearchTerm]);

    React.useEffect(() => {
        if (!project?.tags) return;

        const allCurrentItemIds = new Set();
        const allCurrentTagIds = new Set();

        project.tags.forEach((tag) => {
            allCurrentTagIds.add(tag.id);
            tag.items.forEach((item) => {
                allCurrentItemIds.add(item.id);
            });
        });

        setCheckedItemIds((prev) => {
            const filtered = new Set();
            prev.forEach((id) => {
                if (allCurrentItemIds.has(id)) {
                    filtered.add(id);
                }
            });
            return filtered;
        });

        setCheckedTagIds((prev) => {
            const filtered = new Set();
            prev.forEach((id) => {
                if (allCurrentTagIds.has(id)) {
                    filtered.add(id);
                }
            });
            return filtered;
        });
    }, [project?.tags]);

    const onItemCheckboxToggle = useCallback(
        (itemId, isChecked) => {
            setCheckedItemIds((prev) => {
                const newSet = new Set(prev);
                if (isChecked) {
                    newSet.add(itemId);
                } else {
                    newSet.delete(itemId);
                }
                return newSet;
            });

            if (!isChecked) {
                const tag = (project?.tags ?? []).find((t) =>
                    t.items.some((item) => item.id === itemId),
                );
                if (tag) {
                    setCheckedTagIds((prev) => {
                        if (!prev.has(tag.id)) {
                            return prev;
                        }
                        const newSet = new Set(prev);
                        newSet.delete(tag.id);
                        return newSet;
                    });
                }
            }
        },
        [project?.tags],
    );

    const onTagCheckboxToggle = useCallback(
        (tagId, shouldCheckAll, tagItems) => {
            setCheckedTagIds((prev) => {
                const newSet = new Set(prev);
                if (shouldCheckAll) {
                    newSet.add(tagId);
                } else {
                    newSet.delete(tagId);
                }
                return newSet;
            });

            setCheckedItemIds((prev) => {
                const newSet = new Set(prev);
                tagItems.forEach((item) => {
                    if (shouldCheckAll) {
                        newSet.add(item.id);
                    } else {
                        newSet.delete(item.id);
                    }
                });
                return newSet;
            });
        },
        [],
    );

    React.useEffect(() => {
        if (!project?.tags) return;

        setCheckedTagIds((prev) => {
            if (prev.size === 0) return prev;
            const newSet = new Set(prev);
            project.tags.forEach((tag) => {
                if (!newSet.has(tag.id)) return;
                const allItemsSelected = tag.items.every((item) =>
                    checkedItemIds.has(item.id),
                );
                if (!allItemsSelected) {
                    newSet.delete(tag.id);
                }
            });
            return newSet;
        });
    }, [project?.tags, checkedItemIds]);

    const checkedSkuItems = React.useMemo(() => {
        const skuItems = [];
        for (const itemId of checkedItemIds) {
            for (const tag of project?.tags ?? []) {
                const item = tag.items.find((i) => i.id === itemId);
                if (item && item.productId != null) {
                    skuItems.push({ ...item, tagId: tag.id });
                }
            }
        }
        return skuItems;
    }, [checkedItemIds, project?.tags]);

    const deleteCheckedItems = async () => {
        const totalCount = checkedTagIds.size + checkedItemIds.size;
        if (totalCount === 0) return;

        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: `Deleting ${totalCount} item${
                    totalCount !== 1 ? 's' : ''
                }...`,
            }),
        );
        try {
            const deleteTagPromises = Array.from(checkedTagIds)
                .map((tagId) => {
                    const tag = (project?.tags ?? []).find(
                        (t) => t.id === tagId,
                    );
                    if (tag) {
                        return deleteTag({
                            id: projectId,
                            tag: { id: tagId },
                        }).unwrap();
                    }
                    return null;
                })
                .filter(Boolean);

            const itemsToDelete = [];
            for (const itemId of checkedItemIds) {
                const tag = (project?.tags ?? []).find((t) =>
                    t.items.some((item) => item.id === itemId),
                );
                if (tag && !checkedTagIds.has(tag.id)) {
                    itemsToDelete.push({
                        id: itemId,
                        tagId: tag.id,
                    });
                }
            }

            const deleteItemPromise =
                itemsToDelete.length > 0
                    ? deleteMultipleTagItems({
                          id: projectId,
                          items: itemsToDelete,
                      }).unwrap()
                    : Promise.resolve();

            await Promise.all([...deleteTagPromises, deleteItemPromise]);
            setCheckedItemIds(new Set());
            setCheckedTagIds(new Set());
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const formatCurrency = (amount, locale = 'en-US', currency = 'USD') => {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    React.useEffect(() => {
        let freightStr = '';
        if (projectCountry !== 'Canada')
            freightStr =
                'USD ' + formatCurrency(projectFreight, 'en-US', 'USD');
        else
            freightStr =
                'CAD ' + formatCurrency(projectFreight, 'en-US', 'USD');
        setFreight(freightStr);
    }, [projectFreight, projectCountry]);

    const hubConnection = useHubConnection();

    React.useEffect(() => {
        const handleReceiveItemDocMessage = (jobId, itemId, projectId) => {
            dispatch(
                documentsApi.util.prefetch('getProductDocuments', itemId, {
                    force: true,
                }),
            );
            dispatch(
                documentsApi.util.prefetch(
                    'getProductHasJobInProgress',
                    itemId,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', projectId, {
                    force: true,
                }),
            );
        };
        const sendItemDocAck = (connection, jobId, itemId, isWorkflowJob) => {
            connection.invoke(
                'ItemDocAcknowledge',
                jobId,
                itemId,
                isWorkflowJob,
            );
        };
        const handleReceiveQuoteDocMessage = (jobId, quoteId) => {
            dispatch(
                documentsApi.util.prefetch('getQuoteDocuments', quoteId, {
                    force: true,
                }),
            );
            dispatch(
                documentsApi.util.prefetch(
                    'getQuoteHasJobInProgress',
                    quoteId,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', quoteId, {
                    force: true,
                }),
            );
        };
        const sendQuoteDocAck = (connection, jobId, quoteId) => {
            connection.invoke('QuoteDocAcknowledge', jobId, quoteId);
        };

        if (hubConnection && config?.signalR) {
            hubConnection.then((connection) => {
                connection.on(
                    config.signalR.ItemDoc ?? 'ItemMessage',
                    (jobId, itemId, isWorkflowJob, projectId) => {
                        handleReceiveItemDocMessage(jobId, itemId, projectId);
                        sendItemDocAck(
                            connection,
                            jobId,
                            itemId,
                            isWorkflowJob,
                        );
                    },
                );
                connection.on(
                    config.signalR.QuoteDoc ?? 'QuoteMessage',
                    (jobId, quoteId) => {
                        handleReceiveQuoteDocMessage(jobId, quoteId);
                        sendQuoteDocAck(connection, jobId, quoteId);
                    },
                );
            });
        }

        // Clean up
        return () => {
            if (hubConnection && config?.signalR) {
                hubConnection.then((connection) => {
                    connection.off(
                        config.signalR.ItemDoc ?? 'ItemMessage',
                        handleReceiveItemDocMessage,
                    );
                    connection.off(
                        config.signalR.QuoteDoc ?? 'QuoteMessage',
                        handleReceiveQuoteDocMessage,
                    );
                });
            }
        };
    }, [hubConnection, config, dispatch]);

    const itemsNeedChecking = React.useMemo(() => {
        if (!project) return [];
        if (project.tags.length === 0) return [];
        const result = [];
        const { tags } = project;
        localStorage.setItem('ToValidate', '');
        for (let i = 0; i < tags.length; i++) {
            if (tags[i].items.length > 0) {
                const { items } = tags[i];
                const targets = items.filter((x) => x.needValidate);
                targets.forEach((x) => {
                    result.push(x);
                });
            }
        }
        let toValidate = localStorage.getItem('ToValidate');
        for (let i = 0; i < result.length; i++) {
            localStorage.setItem(
                'ToValidate',
                toValidate === ''
                    ? result[i].id + ','
                    : toValidate + result[i].id + ',',
            );
            toValidate = localStorage.getItem('ToValidate');
            setToValidateItems(toValidate);
        }
        return result;
    }, [project]);

    React.useEffect(() => {
        if (itemsNeedChecking.length > 0) {
            setShowWarnValidateModal(true);
            setFreight('');
        }
    }, [itemsNeedChecking]);

    const onSiteInfoConfiguratorLoaded = React.useCallback(
        (embedConfigurator) => {
            if (project?.siteInfoConfiguratorJson) {
                embedConfigurator.setConfiguredProduct(
                    JSON.parse(project.siteInfoConfiguratorJson),
                );
            } else {
                const defaultSiteInfoConfig = {
                    configurators: [],
                    fields: [
                        {
                            id: '9jr3v06mn',
                            name: 'fCountry',
                            stringValue: 'US',
                        },
                        {
                            id: 'r3pw5luf1',
                            name: 'fState',
                            stringValue: 'Arkansas',
                        },
                        { id: 'el2u5a425', name: 'fCity_US', stringValue: '' },
                        {
                            id: '27q8sa1l',
                            name: 'fProvince',
                            stringValue: 'Ontario',
                        },
                        {
                            id: '535pppdk1',
                            name: 'fCity_CA',
                            stringValue: 'Ailsa Craig',
                        },
                        {
                            id: 'cnzqto4js',
                            name: 'fBuildingCode_US',
                            stringValue: 'IBC 2018',
                        },
                        {
                            id: 'qdemf96k3',
                            name: 'fBuildingCode_CA',
                            stringValue: 'NBC 2010',
                        },
                        {
                            id: 'uy2paey9g',
                            name: 'fBuildingHighestLevel',
                            numberValue: 25,
                        },
                        {
                            id: 'jjii1mkej',
                            name: 'fAverageFloorHeight',
                            numberValue: 10,
                        },
                        { id: '58i6fnw6y', name: 'fWfactor', numberValue: 1 },
                        {
                            id: 'z46pyn90v',
                            name: 'fAsceYear',
                            numberValue: 2016,
                        },
                        {
                            id: 'puurbshzm',
                            name: 'fIsUsgs',
                            booleanValue: false,
                        },
                        {
                            id: 'vvdjkovyn',
                            name: 'fUseDefaultLocation',
                            booleanValue: true,
                        },
                        {
                            id: 'q8hi2c5qh',
                            name: 'fCoordinateOptions',
                            stringValue: 'Default Coordinate',
                        },
                        {
                            id: 'um0vapgf2',
                            name: 'fUsgsLookup',
                            stringValue: 'Latitude/Longitude',
                        },
                        {
                            id: 'spni3soyn',
                            name: 'fUsgsZipCode',
                            stringValue: '',
                        },
                        {
                            id: 'yokkl7qxp',
                            name: 'fUsgsLatitude',
                            numberValue: 34.81,
                        },
                        {
                            id: 'vlh26e4tf',
                            name: 'fUsgsLongitude',
                            numberValue: -92.39,
                        },
                        { id: 'bdc78kh8p', name: 'fSs', numberValue: 0.39 },
                        { id: '84019ndja', name: 'fS1', numberValue: 0.148 },
                        {
                            id: '1p1sffuj5',
                            name: 'fIsSiteClassificationSpecified',
                            booleanValue: false,
                        },
                        {
                            id: 'a88gq1w2y',
                            name: 'fSiteClassification',
                            stringValue: 'D',
                        },
                        { id: 'yuzeqof0t', name: 'fFa', numberValue: 1.49 },
                        { id: 'xy18uv99p', name: 'fFv', numberValue: 2.3 },
                        { id: 'bbapiftq8', name: 'fFa_CA', numberValue: 0 },
                        {
                            id: '2x9swncda',
                            name: 'fOverrideSpectralResponse',
                            booleanValue: false,
                        },
                        { id: 'iggj3awuv', name: 'fSds', numberValue: 0.387 },
                        { id: 'hpcyuj9vj', name: 'fSd1', numberValue: 0.227 },
                        { id: '9godxmntn', name: 'fSa(0.2)', numberValue: 0 },
                        {
                            id: 'pduovbj0x',
                            name: 'fSa(0.2)Calculated',
                            numberValue: 0.13,
                        },
                        {
                            id: 'pj939vabv',
                            name: 'fOverridePGA',
                            booleanValue: false,
                        },
                        { id: 'moni5d12d', name: 'fPGA', numberValue: 0 },
                        {
                            id: 'nzochu6d1',
                            name: 'fPGACalculated',
                            numberValue: 0,
                        },
                        {
                            id: '3dclafyz4',
                            name: 'fBuildingOccupancyRiskCategory',
                            stringValue: 'III',
                        },
                        {
                            id: 'ewa8i87or',
                            name: 'fBuildingImportanceCategory',
                            stringValue: 'Low',
                        },
                        {
                            id: '8hrx2ckyh',
                            name: 'fBuildingImportancefactor_CA',
                            numberValue: 0,
                        },
                        {
                            id: '6ugj82wbh',
                            name: 'fIeFaSa(0.2))',
                            numberValue: 0,
                        },
                        {
                            id: 'lagrn9tze',
                            name: 'fOverrideCalculatedSeismicDesignCategory',
                            booleanValue: false,
                        },
                        {
                            id: 'p1cxw6mf2',
                            name: 'fSeismicDesignCategory',
                            stringValue: 'D',
                        },
                        {
                            id: 'n41wjbsj7',
                            name: 'fOverrideVelocityPressure',
                            booleanValue: false,
                        },
                        {
                            id: 'umb1e3oj6',
                            name: 'freferenceVelocityPressure',
                            numberValue: 0.5,
                        },
                        {
                            id: '965l12fll',
                            name: 'fCalculatedreferenceVelocityPressure',
                            numberValue: 120,
                        },
                        {
                            id: '1ajdjean5',
                            name: 'fWindSpeed',
                            numberValue: 120,
                        },
                        {
                            id: 'k2x4ihl0t',
                            name: 'fExposureCategory',
                            stringValue: 'B',
                        },
                        {
                            id: '835uk78bd',
                            name: 'fExposureCategory_CA',
                            stringValue: 'A',
                        },
                        {
                            id: 'co3t9i37l',
                            name: 'fexposureFactor_CA',
                            numberValue: 30,
                        },
                        {
                            id: '2uvwvp7fh',
                            name: 'fGroundElevationAboveSeaLevel',
                            numberValue: 0,
                        },
                        {
                            id: '5t09xy74r',
                            name: 'fRoofHeightAboveGrade',
                            numberValue: 250,
                        },
                        {
                            id: 'u52wt7ti4',
                            name: 'fImportanceFactor',
                            numberValue: 1,
                        },
                        {
                            id: 'qcy31l56b',
                            name: 'fBuildingHeightFactor',
                            numberValue: 1.9,
                        },
                        {
                            id: 'r3ymps8ws',
                            name: 'fVelocityPressureExposureCoefficient',
                            numberValue: 1.28,
                        },
                        {
                            id: 'st8xg0goo',
                            name: 'fTopographicFactor',
                            numberValue: 1,
                        },
                        {
                            id: 'swlvb3en4',
                            name: 'fgustEffectFactor_CA',
                            numberValue: 0,
                        },
                        {
                            id: 'qhavln9po',
                            name: 'fCheckSiteCondition',
                            booleanValue: true,
                        },
                        {
                            id: 'l5j2f0gv4',
                            name: 'fVM',
                            stringValue:
                                '{"location":{"country":"US","city":"","state":"Arkansas","zipCode":""},"buildingCode":{"name":"IBC 2018","organization":"International Building Code","abbreviation":"IBC","year":2018,"asceYear":2016,"civilYear":0,"wFactor":1},"seismicLoadCalculationParameters_US":{"mappedSpectralResponseAtShortPeriod_Ss":0.39,"mappedSpectralResponseAtPeriodOf1_S1":0.148,"siteClassificationLetter":"D","shortPeriodSiteCoefficient_Fv":2.3,"longPeriodSiteCoefficient_Fa":1.49,"overrideSpectralResponse":false,"designSpectralResponseAtShortPeriod_Sds":0.387,"designSpectralResponseAtPeriodOf1_Sd1":0.227,"buildingOccupancyCategory":"III","overrideSeismicDesignCategory":false,"seismicDesignCategoryLetter":"D","totalNumberOfFloors":25,"isSiteClassificationSpecified":false},"windLoadCalculationParameters_US":{"windSpeedMph":120,"buildingExposureCategory":"B","roofHeightAboveGrade":250,"importanceFactor_I":1,"velocityPressureExposureCoefficient_Kh":1.283972445984872,"topographicFactor_Kzt":1,"gustSpeedPowerLawExponent_Alpha":7,"heightOfAtmosphericBoundryLayer_Zg":1200,"buildingHeightFactor":1.9,"groundElevationFactor_Ke":1}}',
                        },
                        {
                            id: 'xzqvf8ilh',
                            name: 'fSiteConditionsShortDescription',
                            stringValue:
                                '{"siteClass":"D","city":"","state":"Arkansas"}',
                        },
                    ],
                    name: 'Seismic and Wind Parameters',
                    entityType: 'product',
                    idProduct: 351,
                    allowUserToRemove: false,
                    validationMessages: [],
                    addAsQuoteProduct: false,
                    kineticViewOnly: false,
                };
                let country = defaultSiteInfoConfig.fields.find(
                    (c) => c.name === 'fCountry',
                );
                if (country) {
                    country.stringValue =
                        project.country === 'Canada' ? 'CA' : 'US';
                }
                let state = defaultSiteInfoConfig.fields.find(
                    (c) => c.name === 'fState',
                );
                if (state) {
                    state.stringValue = project.state;
                }
                if (project.country === 'Canada') {
                    let prov = defaultSiteInfoConfig.fields.find(
                        (c) => c.name === 'fProvince',
                    );
                    if (prov) {
                        prov.stringValue = project.state;
                    }
                    let city = defaultSiteInfoConfig.fields.find(
                        (c) => c.name === 'fCity_CA',
                    );
                    if (city) {
                        city.stringValue = '';
                    }
                }

                embedConfigurator.setConfiguredProduct(defaultSiteInfoConfig);
            }
        },
        [project],
    );

    const handleNameUpdate = async () => {
        const name = updatedProjectName;
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Saving new name to project and all its items',
            }),
        );
        try {
            await updateProject({ id: projectId, name }).unwrap();
            setIsUpdatingProjectName(false);
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const handleSiteInfoUpdate = async (value) => {
        const siteInfoStr = value?.fields?.find(
            (x) => x.name === 'fSiteConditionsShortDescription',
        )?.stringValue;
        const siteInfo = siteInfoStr ? JSON.parse(siteInfoStr) : undefined;
        setCpqData({ showModal: false });
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Saving site info to project and all items ',
            }),
        );
        try {
            await updateProject({
                id: projectId,
                siteInfo,
                siteInfoConfiguratorJson: JSON.stringify(value),
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const validateAllItems = useCallback(
        async (data, onClose) => {
            let toValidate = toValidateItems; //localStorage.getItem('ToValidate');
            const validating = localStorage.getItem('Validating');
            let validateCount = 0;
            if (validating !== '0') return;
            const markResolve = [];
            const markError = [];
            while (toValidate !== '') {
                const itemIds = toValidate.split(',');
                localStorage.setItem('Validating', itemIds[0]);

                let itemToValidate = data.find((x) => x.id === itemIds[0]);
                setItemsAlreadyValidated(validateCount + 1);
                validateCount++;

                var result = await dispatch(
                    getItemValidate(project, itemToValidate),
                );
                if (result) {
                    const prodConfig = JSON.parse(
                        itemToValidate.configuratorJson,
                    ).configuredProduct;
                    if (sameAsValidatedResult(prodConfig, result)) {
                        markResolve.push({
                            itemId: itemToValidate.id,
                            tagId: itemToValidate.tagId,
                        });
                    } else {
                        markError.push({
                            itemId: itemToValidate.id,
                            tagId: itemToValidate.tagId,
                        });
                    }
                }

                const updated = toValidate.replace(itemIds[0] + ',', '');
                localStorage.setItem('ToValidate', updated);
                toValidate = localStorage.getItem('ToValidate');
            }
            localStorage.setItem('Validating', '0');
            onClose();
            setValidationData({
                itemNum: data.length,
                okItems: markResolve,
                errorItems: markError,
            });
            setShowSaveValidationModal(true);
        },
        [setItemsAlreadyValidated, project, dispatch, toValidateItems],
    );

    return (
        <div className="">
            <ProjectHeader
                parentPath="projects"
                isUpdatingProjectName={isUpdatingProjectName}
                updatedProjectName={updatedProjectName}
                setIsUpdatingProjectName={setIsUpdatingProjectName}
                setUpdatedProjectName={setUpdatedProjectName}
                handleNameUpdate={handleNameUpdate}
                onSiteInfoConfiguratorLoaded={onSiteInfoConfiguratorLoaded}
                handleSiteInfoUpdate={handleSiteInfoUpdate}
                headerOpsDisable={!projectIsReady}
                title={`Freight: ${freight}`}
            />
            <div className="flex gap-4">
                <div className="w-3/5">
                    <Tabs.Context defaultValue="tags">
                        <div className="mb-4">
                            <Tabs.List>
                                <Tabs.Tab
                                    value="tags"
                                    label="Tags"
                                />
                                <Tabs.Tab
                                    value="equipment"
                                    label="Equipment"
                                />
                                <Tabs.Tab
                                    value="room"
                                    label="Room"
                                />
                                <Tabs.Tab
                                    value="trade"
                                    label="Trade"
                                />
                            </Tabs.List>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex gap-3">
                                <button
                                    className="swegon-like-button contained"
                                    onClick={() => setEditSkuModalOpen(true)}
                                    disabled={checkedSkuItems.length === 0}
                                >
                                    Edit SKU ({checkedSkuItems.length})
                                </button>
                                <button
                                    className="swegon-like-button contained"
                                    onClick={deleteCheckedItems}
                                    disabled={
                                        checkedTagIds.size +
                                            checkedItemIds.size ===
                                        0
                                    }
                                >
                                    Delete (
                                    {checkedTagIds.size + checkedItemIds.size})
                                </button>
                            </div>
                            <div className="ml-auto flex items-center justify-end gap-2">
                                <button
                                    onClick={() => setNewTagModalOpen(true)}
                                    className="swegon-like-button contained whitespace-nowrap"
                                >
                                    Add Tag{' '}
                                    <FontAwesomeIcon icon="fa-solid fa-tags" />
                                </button>
                                <ProjectTagsFilter
                                    tags={project?.tags}
                                    setFilteredTags={setFilteredTags}
                                    anchor={
                                        <button className="swegon-like-button contained">
                                            Filter{' '}
                                            <FontAwesomeIcon icon="fa-solid fa-filter" />
                                        </button>
                                    }
                                />
                                <div
                                    className="swegon-ui-text-input-wrapper white h-10"
                                    style={{ width: '220px' }}
                                >
                                    <input
                                        type="text"
                                        value={tagSearchTerm}
                                        onChange={(e) =>
                                            setTagSearchTerm(e.target.value)
                                        }
                                        placeholder="Search tags"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white border border-black-20 p-4">
                            <Tabs.Panel value="tags">
                                <Tags
                                    tags={searchedTags}
                                    projectId={projectId}
                                    checkedItemIds={checkedItemIds}
                                    checkedTagIds={checkedTagIds}
                                    onItemCheckboxToggle={onItemCheckboxToggle}
                                    onTagCheckboxToggle={onTagCheckboxToggle}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="equipment">
                                <EquipmentType
                                    tags={searchedTags}
                                    projectId={projectId}
                                    checkedItemIds={checkedItemIds}
                                    checkedTagIds={checkedTagIds}
                                    onItemCheckboxToggle={onItemCheckboxToggle}
                                    onTagCheckboxToggle={onTagCheckboxToggle}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="room">
                                <Room
                                    tags={searchedTags}
                                    projectId={projectId}
                                    checkedItemIds={checkedItemIds}
                                    checkedTagIds={checkedTagIds}
                                    onItemCheckboxToggle={onItemCheckboxToggle}
                                    onTagCheckboxToggle={onTagCheckboxToggle}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="trade">
                                <Trade
                                    tags={searchedTags}
                                    projectId={projectId}
                                    checkedItemIds={checkedItemIds}
                                    checkedTagIds={checkedTagIds}
                                    onItemCheckboxToggle={onItemCheckboxToggle}
                                    onTagCheckboxToggle={onTagCheckboxToggle}
                                />
                            </Tabs.Panel>
                        </div>
                    </Tabs.Context>
                </div>
                <div className="w-2/5">
                    <Tabs.Context defaultValue="properties">
                        <div className="mb-4">
                            <Tabs.List>
                                <Tabs.Tab
                                    value="properties"
                                    label="Details"
                                />
                                <Tabs.Tab
                                    value="drawings"
                                    label="Drawing Markups"
                                />
                            </Tabs.List>
                        </div>
                        <Tabs.Panel value="properties">
                            <ProductDetailTabs />
                        </Tabs.Panel>
                        <Tabs.Panel value="drawings">
                            <Drawings projectId={projectId} />
                        </Tabs.Panel>
                    </Tabs.Context>
                </div>
            </div>

            <CpqModal
                configuratorId={cpqData.configuratorId}
                viewerClassNames={
                    cpqData.configuratorId === config?.configurators?.Takeoff &&
                    'max-h-[calc(60vh)]'
                }
                show={cpqData.showModal}
                fullScreen={cpqData.fullScreen}
                onConfiguratorLoaded={cpqData.onConfiguratorLoaded}
                configuredProduct={cpqData.configuredProduct}
                projectInfo={cpqData.projectInfo}
                projectSiteInfoJson={cpqData.projectSiteInfoJson}
                onSubmit={cpqData.onSubmit}
                onClose={() => {
                    setCpqData({ showModal: false });
                }}
            />
            <NewTagModal
                show={newTagModalOpen}
                onClose={() => setNewTagModalOpen(false)}
                projectId={projectId}
                tags={project?.tags}
                tradeList={config?.options.tradeList}
                equipmentList={config?.options.equipmentList}
            />
            <EditSkuModal
                show={editSkuModalOpen}
                onClose={() => setEditSkuModalOpen(false)}
                projectId={projectId}
                skuItems={checkedSkuItems}
            />
            <ValidateWarningModal
                show={showWarnValidateModal}
                onClose={() => setShowWarnValidateModal(false)}
                onValidate={() => {
                    setShowWarnValidateModal(false);
                    setShowValidatingModal(true);
                }}
                items={itemsNeedChecking}
            />
            <ValidatingModal
                show={showValidatingModal}
                onClose={() => setShowValidatingModal(false)}
                onValidateItem={() => {
                    localStorage.setItem('Validating', '0');
                    validateAllItems(itemsNeedChecking, () =>
                        setShowValidatingModal(false),
                    );
                }}
                itemsToValidate={itemsNeedChecking}
                itemsValidated={itemsAlreadyValidated}
            />
            <SaveValidationModal
                show={showSaveValidationModal}
                data={validationData}
                projectId={projectId}
                onClose={() => setShowSaveValidationModal(false)}
            />
        </div>
    );
};

const ProjectWrapped = cpqDataState.withStateContext(Project);

export { ProjectWrapped as Project };
